// store/callListSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface Branch {
  id: number
  company_name: string
}

type ListSource =
  | { type: 'branch'; branchId: string }
  | { type: 'all'; callReason: string }
  | { type: 'followUp' }
  | { type: 'bdFalcon'; callingListId: string }
  | { type: 'customList'; customListId: number }
  | { type: 'idList' }
  | { type: 'unknown' }

export type PowerDialerState = 'INITIAL' | 'IN_CALL' | 'TIMER_RUNNING' | 'PAUSED'

export const DEFAULT_TIME_TO_NEXT_CALL = 8 * 1000 // 8 seconds

interface CallListState {
  isOpen: boolean
  currentIndex: number
  callList: Branch[]
  startTime: number | null
  listName: string | null
  listSource: ListSource
  isLoadingMore: boolean
  // Powerdialer state
  powerDialerState: PowerDialerState
  timeToNextCall: number
}

const initialState: CallListState = {
  isOpen: false,
  currentIndex: 0,
  callList: [],
  startTime: null,
  listName: null,
  listSource: { type: 'unknown' },
  isLoadingMore: false,
  // Powerdialer initial state
  powerDialerState: 'INITIAL',
  timeToNextCall: DEFAULT_TIME_TO_NEXT_CALL,
}

const callListSlice = createSlice({
  name: 'callList',
  initialState,
  reducers: {
    close(state, action: PayloadAction<boolean>) {
      state.isOpen = action.payload
    },
    open(state, action: PayloadAction<boolean>) {
      state.isOpen = action.payload
    },
    next(state) {
      if (
        state.callList.length > 0 &&
        state.currentIndex < state.callList.length - 1
      ) {
        state.currentIndex = state.currentIndex + 1
      }
    },
    prev(state) {
      if (state.callList.length > 0 && state.currentIndex > 0) {
        state.currentIndex = state.currentIndex - 1
      }
    },
    setCallList(state, action: PayloadAction<{ leads: Branch[]; listName?: string; listSource?: ListSource }>) {
      // Deduplicate leads by id
      const seen = new Set<number>()
      const uniqueLeads = action.payload.leads.filter(l => {
        if (seen.has(l.id)) return false
        seen.add(l.id)
        return true
      })
      state.callList = uniqueLeads
      state.currentIndex = 0
      state.startTime = Date.now()
      state.listName = action.payload.listName || null
      state.listSource = action.payload.listSource || { type: 'unknown' }
    },
    appendToCallList(state, action: PayloadAction<Branch[]>) {
      // Deduplicate by id
      const existingIds = new Set(state.callList.map(l => l.id))
      const newLeads = action.payload.filter(l => !existingIds.has(l.id))
      state.callList = [...state.callList, ...newLeads]
      state.isLoadingMore = false
    },
    setLoadingMore(state, action: PayloadAction<boolean>) {
      state.isLoadingMore = action.payload
    },
    insertAtNextIndex(state, action: PayloadAction<Branch>) {
      const nextIndex = state.currentIndex + 1
      state.callList.splice(nextIndex, 0, action.payload)
    },
    clearCallList(state) {
      state.callList = []
      state.currentIndex = 0
      state.startTime = null
      state.listName = null
      state.listSource = { type: 'unknown' }
      state.isOpen = false
      state.isLoadingMore = false
      state.powerDialerState = 'INITIAL'
      state.timeToNextCall = DEFAULT_TIME_TO_NEXT_CALL
    },
    // Powerdialer actions
    setPowerDialerState(state, action: PayloadAction<PowerDialerState | { state: PowerDialerState; timeout?: number }>) {
      const newState = typeof action.payload === 'string' ? action.payload : action.payload.state
      const timeout = typeof action.payload === 'object' ? action.payload.timeout : undefined
      state.powerDialerState = newState
      // Reset timer when starting fresh
      if (newState === 'TIMER_RUNNING') {
        state.timeToNextCall = timeout ?? DEFAULT_TIME_TO_NEXT_CALL
      }
    },
    tickPowerDialer(state) {
      if (state.powerDialerState === 'TIMER_RUNNING' && state.timeToNextCall > 0) {
        state.timeToNextCall = state.timeToNextCall - 100
      }
    },
    resetPowerDialerTimer(state, action: PayloadAction<number | undefined>) {
      state.timeToNextCall = action.payload ?? DEFAULT_TIME_TO_NEXT_CALL
    },
    setCurrentIndex(state, action: PayloadAction<number>) {
      const index = action.payload
      if (index >= 0 && index < state.callList.length) {
        state.currentIndex = index
      }
    }
  }
})

export const {
  open,
  next,
  prev,
  setCallList,
  appendToCallList,
  setLoadingMore,
  insertAtNextIndex,
  clearCallList,
  setPowerDialerState,
  tickPowerDialer,
  resetPowerDialerTimer,
  setCurrentIndex
} = callListSlice.actions
export default callListSlice.reducer

export const selectCallListSource = (state: { callList: CallListState }) => state.callList.listSource
